import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { CreateFileAttachmentInput } from '@releve/shared';
import { supabase } from '@/integrations/supabase/client';
import { authedDelete, authedPost } from '@/lib/api';
import { vaultAttachmentsPath, vaultFilePath } from '@/hooks/useVaultMutations';
import { toast } from '@/hooks/useToast';
import { invalidateAuditCaches } from '@/lib/auditCacheKeys';
import { storageSafeFileName } from '@releve/shared';

/**
 * WRITES TO `files` / `asset_attachments` GO THROUGH THE BACKEND. `supabase`
 * survives here for the storage upload (browser-side by design;
 * docs/BACKEND_API.md) and the junction SELECTs.
 *
 * AUDIT: attaching / detaching is audited by the `trg_audit_asset_attachments`
 * trigger (ATTACH / DETACH rows in `asset_audit`, docs/AUDIT.md) — never
 * insert `asset_audit` rows from here; the table grants SELECT only.
 */

export type AssetAttachmentType = 'purchase_receipt' | 'manual' | 'warranty' | 'photo';

export interface AssetAttachment {
  id: string;
  asset_id: string;
  file_id: string;
  attachment_type: AssetAttachmentType;
  created_at: string;
  created_by: string;
  file: {
    id: string;
    name: string;
    file_path: string;
    file_size: number;
    content_type: string;
  };
}

/**
 * Hook to fetch attachments for an asset
 */
export const useAssetAttachments = (assetId?: string) => {
  return useQuery({
    queryKey: ['asset-attachments', assetId],
    queryFn: async () => {
      if (!assetId) return [];

      const { data, error } = await supabase
        .from('asset_attachments')
        .select(`
          *,
          file:files(id, name, file_path, file_size, content_type)
        `)
        .eq('asset_id', assetId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return (data || []) as AssetAttachment[];
    },
    enabled: !!assetId,
  });
};

/**
 * Hook to upload attachments for an asset
 * Supports uploading files with individual attachment types
 */
export const useUploadAssetAttachments = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      organizationId,
      assetId,
      filesWithTypes,
    }: {
      organizationId: string;
      assetId: string;
      filesWithTypes: Array<{ file: File; type: AssetAttachmentType }>;
    }) => {
      // No `supabase.auth.getUser()` here: `created_by` is pinned server-side
      // from the verified token, so the browser never supplies an identity.
      const uploadedFiles: AssetAttachment[] = [];

      for (const { file, type: attachmentType } of filesWithTypes) {
        try {
          // 1. Upload file to storage
          // Key-safe object name (Storage rejects e.g. the narrow no-break
          // space in macOS screenshot names); `name` below keeps the original.
          const safeName = storageSafeFileName(file.name);
          const storagePath = `${organizationId}/assets/${assetId}/${safeName}`;
          const { error: uploadError } = await supabase.storage
            .from('files')
            .upload(storagePath, file, {
              cacheControl: '3600',
              upsert: false,
            });

          let finalPath = storagePath;
          let finalFileName = file.name;

          if (uploadError) {
            // If file already exists, try with timestamp
            const timestamp = Date.now();
            const fileExt = file.name.split('.').pop();
            const fileNameWithoutExt = file.name.substring(0, file.name.lastIndexOf('.'));
            finalFileName = `${fileNameWithoutExt}-${timestamp}.${fileExt}`;
            const safeDot = safeName.lastIndexOf('.');
            const safeStem = safeDot > 0 ? safeName.slice(0, safeDot) : safeName;
            const safeExt = safeDot > 0 ? safeName.slice(safeDot) : '';
            finalPath = `${organizationId}/assets/${assetId}/${safeStem}-${timestamp}${safeExt}`;

            const { error: retryError } = await supabase.storage
              .from('files')
              .upload(finalPath, file, {
                cacheControl: '3600',
                upsert: false,
              });

            if (retryError) throw retryError;
          }

          // 2. Create the file row AND the junction row in ONE atomic call.
          //    This is where the two structurally-identical insert arms this
          //    hook used to carry (retry path vs first-try path, differing
          //    only in which name/path pair they wrote) collapse into one.
          //    `file_type` is NOT sent — the handler resolves it to the
          //    attachment_type, and the body schema is `.strict()`.
          const { attachment } = await authedPost<{ attachment: AssetAttachment }>(
            vaultAttachmentsPath(organizationId),
            {
              parent: { kind: 'asset', asset_id: assetId },
              attachment_type: attachmentType,
              file: {
                name: finalFileName,
                file_path: finalPath,
                file_size: file.size,
                content_type: file.type,
              },
            } satisfies CreateFileAttachmentInput,
          );

          uploadedFiles.push(attachment);
        } catch (error) {
          console.error(`Failed to upload file ${file.name}:`, error);
          // Continue with other files even if one fails
        }
      }

      return uploadedFiles;
    },
    onSuccess: (_uploadedFiles, { organizationId, assetId }) => {
      queryClient.invalidateQueries({ queryKey: ['asset-attachments', assetId] });
      // The rows these uploads create carry organization_id, so they show up in
      // the Vault — which never used to be refreshed after an upload.
      queryClient.invalidateQueries({ queryKey: ['files', organizationId] });
      // The trigger-written ATTACH / DETACH rows show in the asset's History.
      invalidateAuditCaches(queryClient, organizationId);

      toast.success('Files uploaded successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to upload attachments');
    },
  });
};

/**
 * Hook to delete an asset attachment
 */
export const useDeleteAssetAttachment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      organizationId,
      attachmentId,
    }: {
      organizationId: string;
      attachmentId: string;
      /** Not used by the write — it is the invalidation key. */
      assetId: string;
    }) => {
      // 1. Get the file id the delete addresses. A READ, so it stays on
      //    supabase.
      const { data: attachment, error: readError } = await supabase
        .from('asset_attachments')
        .select('file_id')
        .eq('id', attachmentId)
        .single();

      if (readError) throw readError;
      // The old code SILENTLY no-oped here, so a vanished junction row looked
      // like a successful delete. Now it surfaces through the error toast.
      if (!attachment?.file_id) {
        throw new Error('Attachment not found');
      }

      // 2. ONE call: the backend pre-reads the row, best-effort removes the
      //    storage object and deletes the FILES row. The junction row goes
      //    with it — asset_attachments.file_id is ON DELETE CASCADE, so the
      //    explicit junction delete is the redundant statement, never the
      //    files delete (dropping THAT would orphan the file row in the Vault).
      //
      //    `?attachment_id` says WHICH attachment this delete comes from. The
      //    endpoint counts the file's OTHER references and 409s if any remain:
      //    a file only this asset holds is deleted, one something else also
      //    holds is refused. The Vault's own Delete omits the parameter and is
      //    therefore refused for any attached file at all.
      await authedDelete<unknown>(
        vaultFilePath(organizationId, attachment.file_id, attachmentId),
      );
    },
    onSuccess: (_, { organizationId, assetId }) => {
      queryClient.invalidateQueries({ queryKey: ['asset-attachments', assetId] });
      queryClient.invalidateQueries({ queryKey: ['files', organizationId] });
      // The trigger-written ATTACH / DETACH rows show in the asset's History.
      invalidateAuditCaches(queryClient, organizationId);

      toast.success('Attachment deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete attachment');
    },
  });
};
